"use client";

import React, { useState, useEffect, useCallback } from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { MapPin, Building2, ArrowLeft, Image as ImageIcon } from 'lucide-react';

declare const wvcClient: any;

function RelatedProjectsConsumer({ currentCategory }: { currentCategory?: string }) {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
        {[1, 2, 3].map((_, i) => (
          <div key={i} data-index={i} className="h-64 bg-muted/50 animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }

  if (error || posts.length === 0) {
    return null;
  }

  return (
    <div className="mt-16 pt-12 border-t border-border">
      <div className="flex items-center justify-between mb-8">
        <h3 className="text-2xl font-default font-semibold text-foreground">
          Related Projects
        </h3>
        <Link to="/ProjectsPage">
          <Button variant="ghost" className="text-muted-foreground hover:text-foreground">
            View All
          </Button>
        </Link>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {posts.slice(0, 3).map((post, i) => {
          const category = post.customFields?.category || currentCategory || "Project";
          return (
            <Card key={post.id} data-index={i} className="overflow-hidden group border-border/50 shadow-sm hover:shadow-md transition-shadow">
              <Link to={post.link || "#"} className="block">
                <div className="aspect-[4/3] relative overflow-hidden bg-muted">
                  {post.featuredImage ? (
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <ImageIcon className="size-8 opacity-20" />
                    </div>
                  )}
                  <div className="absolute top-4 left-4">
                    <Badge variant="secondary" className="bg-background/90 backdrop-blur-sm text-foreground border-none shadow-sm">
                      {category}
                    </Badge>
                  </div>
                </div>
                <CardContent className="p-5">
                  <h4 className="font-default font-medium text-lg text-foreground line-clamp-1 mb-2">
                    {post.title}
                  </h4>
                  {post.customFields?.location && (
                    <div className="flex items-center text-sm text-muted-foreground">
                      <MapPin className="size-3.5 mr-1.5" />
                      <span className="line-clamp-1">{post.customFields.location}</span>
                    </div>
                  )}
                </CardContent>
              </Link>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function RelatedProjects({ category }: { category?: string }) {
  const wp_query = new WP_Query({
    post_type: "project",
    per_page: 4,
  });

  return (
    <PostsProvider wp_query={{ post_type: "project", per_page: 4, status: ["publish"] }}>
      <RelatedProjectsConsumer currentCategory={category} />
    </PostsProvider>
  );
}

function ProjectGallery({ images }: { images: string[] }) {
  const [mainApi, setMainApi] = useState<CarouselApi>();
  const [thumbApi, setThumbApi] = useState<CarouselApi>();
  const [selectedIndex, setSelectedIndex] = useState(0);

  const onThumbClick = useCallback(
    (index: number) => {
      if (!mainApi || !thumbApi) return;
      mainApi.scrollTo(index);
    },
    [mainApi, thumbApi]
  );

  const onSelect = useCallback(() => {
    if (!mainApi || !thumbApi) return;
    setSelectedIndex(mainApi.selectedScrollSnap());
    thumbApi.scrollTo(mainApi.selectedScrollSnap());
  }, [mainApi, thumbApi, setSelectedIndex]);

  useEffect(() => {
    if (!mainApi) return;
    onSelect();
    mainApi.on("select", onSelect);
    mainApi.on("reInit", onSelect);
  }, [mainApi, onSelect]);

  if (!images || images.length === 0) return null;

  return (
    <div className="w-full space-y-4 my-12">
      <Carousel setApi={setMainApi} className="w-full bg-muted/30 rounded-xl overflow-hidden border border-border/50">
        <CarouselContent>
          {images.map((imgSrc, index) => {
            const img = { src: imgSrc, srcset: "", sizes: "" };
            return (
              <CarouselItem key={index} data-index={index}>
                <div className="aspect-[16/9] md:aspect-[21/9] relative w-full flex items-center justify-center overflow-hidden">
                  <img
                    src={img.src}
                    data-wvc-srcset={img.srcset}
                    data-wvc-sizes={img.sizes}
                    alt={`Gallery image ${index + 1}`}
                    className="w-full h-full object-cover"
                    loading={index === 0 ? "eager" : "lazy"}
                  />
                </div>
              </CarouselItem>
            );
          })}
        </CarouselContent>
        <div className="hidden md:block">
          <CarouselPrevious className="left-4 bg-background/80 backdrop-blur-sm border-none shadow-md hover:bg-background" />
          <CarouselNext className="right-4 bg-background/80 backdrop-blur-sm border-none shadow-md hover:bg-background" />
        </div>
      </Carousel>

      {images.length > 1 && (
        <Carousel
          setApi={setThumbApi}
          opts={{
            align: "start",
            containScroll: "keepSnaps",
            dragFree: true,
          }}
          className="w-full"
        >
          <CarouselContent className="-ml-2 md:-ml-4">
            {images.map((imgSrc, index) => {
              const img = { src: imgSrc, srcset: "", sizes: "" };
              return (
                <CarouselItem
                  key={index}
                  data-index={index}
                  className="pl-2 md:pl-4 basis-1/3 md:basis-1/5 lg:basis-1/6"
                >
                  <Button
                    onClick={() => onThumbClick(index)}
                    className={`relative flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-lg border-2 transition-all p-0 h-auto ${
                      index === selectedIndex
                        ? "border-primary shadow-sm"
                        : "border-transparent opacity-60 hover:opacity-100"
                    }`}
                    variant="ghost"
                    aria-label={`Go to slide ${index + 1}`}
                  >
                    <img
                      src={img.src}
                      data-wvc-srcset={img.srcset}
                      data-wvc-sizes={img.sizes}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </Button>
                </CarouselItem>
              );
            })}
          </CarouselContent>
        </Carousel>
      )}
    </div>
  );
}

function SingleProjectConsumer() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-12 md:py-24 max-w-5xl animate-pulse">
        <div className="h-6 w-48 bg-muted rounded mb-8" />
        <div className="h-12 w-3/4 bg-muted rounded mb-6" />
        <div className="flex gap-4 mb-12">
          <div className="h-8 w-24 bg-muted rounded-lg" />
          <div className="h-8 w-32 bg-muted rounded-lg" />
        </div>
        <div className="aspect-[21/9] w-full bg-muted rounded-xl mb-12" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted rounded" />
          <div className="h-4 w-full bg-muted rounded" />
          <div className="h-4 w-2/3 bg-muted rounded" />
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 py-24 text-center max-w-2xl">
        <h2 className="text-2xl font-default font-semibold mb-4 text-foreground">Project Not Found</h2>
        <p className="text-muted-foreground mb-8">
          The project you are looking for does not exist or has been removed.
        </p>
        <Link to="/ProjectsPage">
          <Button>
            <ArrowLeft className="mr-2 size-4" />
            Back to Projects
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];
  const title = post.title;
  const location = post.customFields?.location;
  const projectType = post.customFields?.project_type;
  const category = post.customFields?.category;
  const description = post.customFields?.description || post.content;
  
  const mainImageSrc = post.featuredImage || post.customFields?.main_image;
  
  let galleryImages: string[] = [];
  if (Array.isArray(post.customFields?.gallery_images)) {
    galleryImages = post.customFields.gallery_images;
  } else if (typeof post.customFields?.gallery_images === "string" && post.customFields.gallery_images.trim() !== "") {
    galleryImages = [post.customFields.gallery_images];
  }

  if (galleryImages.length === 0 && mainImageSrc) {
    galleryImages = [mainImageSrc];
  }

  return (
    <article className="container mx-auto px-4 py-12 md:py-20 max-w-5xl">
      <div className="mb-8 md:mb-12">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/ProjectsPage">Projects</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="line-clamp-1 max-w-[200px] md:max-w-none">
                {title}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <header className="mb-10 md:mb-16">
        <h1 className="text-4xl md:text-5xl lg:text-6xl font-default font-bold text-foreground tracking-tight mb-6 leading-tight">
          {title}
        </h1>
        
        <div className="flex flex-wrap items-center gap-3 md:gap-4">
          {location && (
            <Badge variant="outline" className="px-3 py-1.5 text-sm bg-background border-border text-muted-foreground">
              <MapPin className="size-4 mr-1.5 text-primary" />
              {location}
            </Badge>
          )}
          {projectType && (
            <Badge variant="outline" className="px-3 py-1.5 text-sm bg-background border-border text-muted-foreground">
              <Building2 className="size-4 mr-1.5 text-primary" />
              {projectType}
            </Badge>
          )}
          {category && (
            <Badge variant="secondary" className="px-3 py-1.5 text-sm">
              {category}
            </Badge>
          )}
        </div>
      </header>

      {galleryImages.length === 0 && mainImageSrc && (
        <div className="w-full aspect-[16/9] md:aspect-[21/9] relative rounded-xl overflow-hidden mb-12 border border-border/50 shadow-sm">
          <img
            src={mainImageSrc}
            alt={title}
            className="w-full h-full object-cover"
            loading="eager"
          />
        </div>
      )}

      {galleryImages.length > 0 && (
        <ProjectGallery images={galleryImages} />
      )}

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12 md:mt-20">
        <div className="lg:col-span-8">
          <h2 className="text-2xl font-default font-semibold text-foreground mb-6">
            About This Project
          </h2>
          {description ? (
            <div 
              className="prose prose-neutral dark:prose-invert max-w-none text-muted-foreground leading-relaxed whitespace-pre-wrap font-sans"
              dangerouslySetInnerHTML={{ __html: description }}
            />
          ) : (
            <p className="text-muted-foreground italic">No description available for this project.</p>
          )}
        </div>
        
        <div className="lg:col-span-4">
          <Card className="bg-muted/30 border-border/50 shadow-sm sticky top-24">
            <CardContent className="p-6 md:p-8 space-y-6">
              <h3 className="font-default font-semibold text-lg text-foreground border-b border-border pb-4">
                Project Details
              </h3>
              
              <dl className="space-y-4 text-sm">
                {location && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="text-muted-foreground font-medium">Location</dt>
                    <dd className="col-span-2 text-foreground font-medium text-right">{location}</dd>
                  </div>
                )}
                {projectType && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="text-muted-foreground font-medium">Type</dt>
                    <dd className="col-span-2 text-foreground font-medium text-right">{projectType}</dd>
                  </div>
                )}
                {category && (
                  <div className="grid grid-cols-3 gap-2">
                    <dt className="text-muted-foreground font-medium">Category</dt>
                    <dd className="col-span-2 text-foreground font-medium text-right">{category}</dd>
                  </div>
                )}
              </dl>

              <Separator className="my-6" />
              
              <div className="pt-2">
                <Link to="/ContactUsPage" className="w-full block">
                  <Button className="w-full" size="lg">
                    Discuss a Similar Project
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <div className="mt-16 pt-8 border-t border-border">
        <Link to="/ProjectsPage">
          <Button variant="outline" className="group">
            <ArrowLeft className="mr-2 size-4 transition-transform group-hover:-translate-x-1" />
            Back to All Projects
          </Button>
        </Link>
      </div>

      <RelatedProjects category={category} />
    </article>
  );
}

export default function ProjectTemplate() {
  let wp_query;
  try {
    const currentWPQueryParams = wvcClient.getCurrentWPQueryParams();
    wp_query = new WP_Query(currentWPQueryParams);
  } catch (e) {
    wp_query = new WP_Query({ post_type: "project", per_page: 1 });
  }

  return (
    <section id="single-project-template-section" className="bg-background min-h-screen">
      <PostsProvider wp_query={{ post_type: "project", per_page: 4, status: ["publish"] }}>
        <SingleProjectConsumer />
      </PostsProvider>
    </section>
  );
}